/**
 * Helper component for rendering a debounced search input
 * Waits `debounceTime` ms before firing change event
 */

// import primary libraries
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import StyledButton from 'shared-resources/components/src/StyledButton';

// import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { useCallback } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const SearchInput = ({
  autoFocus
  , change
  , disabled
  , helpText
  , name
  , placeholder = "Search"
  , required
  , value = ''
  , debounceTime = 0
  , filters = []
}) => {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState('');
  const handleChange = useCallback((newValue) => {
    change({ target: { name, value: newValue } });
  }, [change, name])

  useEffect(() => {
    // Fire change event after delay
    const handler = setTimeout(() => {
      if (value !== undefined && debouncedValue !== value) {
        handleChange(debouncedValue);
      }
    }, debounceTime);
    // Cancel the timeout if value changes (also on delay change or unmount)
    return () => {
      clearTimeout(handler);
    };
  }, [handleChange, debouncedValue, debounceTime]);

  useEffect(() => {
    if (value !== debouncedValue) {
      setDebouncedValue(value);
    }
  }, [value]); 

  return (
    <>
      <div
        className="relative w-full lg:w-auto border-transparent z-30"
      >
        <MagnifyingGlassIcon className="absolute top-0 left-0 w-4 my-auto h-full ml-5 text-neutral-ter" />
        <input
          autoFocus={autoFocus}
          disabled={disabled}
          name={name}
          // send the event immediately when the user presses the enter key
          onKeyPress={(e) => e.key === 'Enter' && handleChange(debouncedValue)}
          onChange={(e) => setDebouncedValue(e.target.value)}
          placeholder={placeholder}
          required={required}
          type="text"
          value={debouncedValue}
          className={`px-2 pl-10 text-sm text-left py-1 block w-full mt-0 rounded-full !bg-neutral-pri`}
        />
        {helpText && <small className="block"><em>{helpText}</em></small>}
        {filters.length > 0 && (
          <div className="p-2 bg-white z-20 relative">
            {filters.map((filter, index) => (
              <StyledButton
                key={index}
                variant={filter.active ? 'primary' : 'text'}
                size="xs"
                className="m-1"
                onClick={(e) => {
                  e.preventDefault();
                  filter.onClick();
                }}
              >
                {filter.label}
              </StyledButton>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

SearchInput.propTypes = {
  change: PropTypes.func.isRequired
  , disabled: PropTypes.bool
  , helpText: PropTypes.any
  // , label: PropTypes.string // search inputs don't have labels, only placeholders
  , name: PropTypes.string.isRequired
  , placeholder: PropTypes.string
  , required: PropTypes.bool
  , value: PropTypes.string
  , debounceTime: PropTypes.number
  , filters: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      active: PropTypes.bool.isRequired,
      onClick: PropTypes.func.isRequired
  }))
}

SearchInput.defaultProps = {
  disabled: false
  , helpText: null
  , label: ''
  , placeholder: ''
  , required: false
}

export default SearchInput;
